import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { searchDynamicPricing, getDynamicPricingFilters, getDynamicPricingById } from "../../../core/actionCreators/dynamicPricingActionCreator";
import Loader from "../Loader/Loader";
import CommonPagination from "../CommonPagination/CommonPagination";
import { GET_DYNAMIC_PRICING_DEFAULT_PAGE_SIZE, DYNAMIC_PRICING_PAGE_HEADERS } from "../../../utils/constants";
import DynamicPricingListItem from "./DynamicPricingListItem";
import DynamicPricingSubHeader from "./DynamicPricingSubHeader";
import { toast } from "react-hot-toast";
import Select from "react-select";

const EMPTY_FILTERS = {
    l1Category: [] as string[],
    l2Category: [] as string[],
    l3Category: [] as string[],
    l4Category: [] as string[],
    minPrice: "",
    maxPrice: "",
}

// react-select ships with its own 38px control. These bring it in line with the
// 32px / 12px form controls the rest of the listing pages use.
const SELECT_STYLES = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 32,
        borderRadius: 2,
        fontSize: 12,
        boxShadow: "none",
        borderColor: state.isFocused ? "#60a5fa" : "#d1d5db",
        backgroundColor: state.isDisabled ? "#f3f4f6" : "#fff",
        ":hover": { borderColor: state.isFocused ? "#60a5fa" : "#d1d5db" },
    }),
    valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    placeholder: (base: any) => ({ ...base, fontSize: 12, color: "#9ca3af" }),
    multiValue: (base: any) => ({ ...base, backgroundColor: "#eff6ff", borderRadius: 2 }),
    multiValueLabel: (base: any) => ({ ...base, fontSize: 11, color: "#1d4ed8", padding: "1px 4px" }),
    multiValueRemove: (base: any) => ({ ...base, color: "#1d4ed8", ":hover": { backgroundColor: "#dbeafe", color: "#1e40af" } }),
    dropdownIndicator: (base: any) => ({ ...base, padding: 4 }),
    clearIndicator: (base: any) => ({ ...base, padding: 4 }),
    indicatorSeparator: (base: any) => ({ ...base, marginTop: 6, marginBottom: 6 }),
    menu: (base: any) => ({ ...base, fontSize: 12, marginTop: 2 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any) => ({ ...base, fontSize: 12, padding: "6px 10px" }),
}

const DynamicPricingList = () => {
    const dispatch = useDispatch();
    const { isLoading, dynamicPricingList, dynamicPricingFilters } = useSelector((s: any) => s.dynamicPricingStore);

    // `selected*` / `priceFilters` / `msnInput` are what the user is typing;
    // `searchCriteria` is what has actually been searched for. Only Search /
    // Clear Filters / pagination move one into the other.
    const [selectedL1, setSelectedL1] = useState<any[]>([]);
    const [selectedL2, setSelectedL2] = useState<any[]>([]);
    const [selectedL3, setSelectedL3] = useState<any[]>([]);
    const [selectedL4, setSelectedL4] = useState<any[]>([]);
    const [priceFilters, setPriceFilters] = useState({ minPrice: "", maxPrice: "" });
    const [msnInput, setMsnInput] = useState("");

    // An exact MSN lookup replaces the listing with a single row, so pagination
    // and the filter-driven refetch are suspended while it is on.
    const [isSearchingMsn, setIsSearchingMsn] = useState(false);

    const [searchCriteria, setSearchCriteria] = useState({
        ...EMPTY_FILTERS,
        page: 0,
        size: GET_DYNAMIC_PRICING_DEFAULT_PAGE_SIZE,
    } as any);

    const l1Options = React.useMemo(() => {
        return (dynamicPricingFilters || []).map((cat: any) => ({
            label: cat.categoryName,
            value: cat.categoryName,
            subCategories: cat.subCategories
        }));
    }, [dynamicPricingFilters]);

    const l2Options = React.useMemo(() => {
        const selected = selectedL1.map(s => s.value);
        return l1Options
            .filter((c: any) => selected.includes(c.value))
            .flatMap((c: any) => c.subCategories || [])
            .map((cat: any) => ({
                label: cat.categoryName,
                value: cat.categoryName,
                subCategories: cat.subCategories
            }));
    }, [l1Options, selectedL1]);

    const l3Options = React.useMemo(() => {
        const selected = selectedL2.map(s => s.value);
        return l2Options
            .filter((c: any) => selected.includes(c.value))
            .flatMap((c: any) => c.subCategories || [])
            .map((cat: any) => ({
                label: cat.categoryName,
                value: cat.categoryName,
                subCategories: cat.subCategories
            }));
    }, [l2Options, selectedL2]);

    const l4Options = React.useMemo(() => {
        const selected = selectedL3.map(s => s.value);
        return l3Options
            .filter((c: any) => selected.includes(c.value))
            .flatMap((c: any) => c.subCategories || [])
            .map((cat: any) => ({
                label: cat.categoryName,
                value: cat.categoryName,
                subCategories: cat.subCategories
            }));
    }, [l3Options, selectedL3]);

    useEffect(() => {
        // Fetch initial filters (full hierarchy)
        dispatch(getDynamicPricingFilters({
            l1Category: [],
            l2Category: [],
            l3Category: []
        }, null));
    }, [dispatch]);

    useEffect(() => {
        dispatch(searchDynamicPricing({
            page: searchCriteria.page,
            size: searchCriteria.size,
            l1Category: searchCriteria.l1Category,
            l2Category: searchCriteria.l2Category,
            l3Category: searchCriteria.l3Category,
            l4Category: searchCriteria.l4Category,
            minPrice: searchCriteria.minPrice ? Number(searchCriteria.minPrice) : undefined,
            maxPrice: searchCriteria.maxPrice ? Number(searchCriteria.maxPrice) : undefined,
        }, null));
    }, [searchCriteria, dispatch]);

    // An MSN takes precedence: it identifies a single mapping, so the category /
    // price filters have nothing left to narrow down.
    const onSearch = () => {
        const msn = msnInput.trim();
        if (msn) {
            setIsSearchingMsn(true);
            dispatch(getDynamicPricingById(msn, (success: boolean) => {
                if (!success) toast.error("MSN not found");
            }));
            return;
        }
        setIsSearchingMsn(false);
        setSearchCriteria({
            ...searchCriteria,
            page: 0,
            l1Category: selectedL1.map(s => s.value),
            l2Category: selectedL2.map(s => s.value),
            l3Category: selectedL3.map(s => s.value),
            l4Category: selectedL4.map(s => s.value),
            minPrice: priceFilters.minPrice,
            maxPrice: priceFilters.maxPrice,
        });
    };

    const onFilterKeyDown = (e: any) => {
        if (e.key === "Enter") onSearch();
    };

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (pageNumber: any) => {
        setSearchCriteria({ ...searchCriteria, page: Math.max(0, Number(pageNumber) - 1) });
    };

    const hasActiveFilters = !!msnInput || isSearchingMsn
        || selectedL1.length > 0 || selectedL2.length > 0 || selectedL3.length > 0 || selectedL4.length > 0
        || !!priceFilters.minPrice || !!priceFilters.maxPrice
        || searchCriteria.l1Category.length > 0 || searchCriteria.l2Category.length > 0
        || searchCriteria.l3Category.length > 0 || searchCriteria.l4Category.length > 0
        || !!searchCriteria.minPrice || !!searchCriteria.maxPrice;

    const clearFilters = () => {
        setSelectedL1([]);
        setSelectedL2([]);
        setSelectedL3([]);
        setSelectedL4([]);
        setPriceFilters({ minPrice: "", maxPrice: "" });
        setMsnInput("");
        setIsSearchingMsn(false);
        setSearchCriteria({ ...searchCriteria, ...EMPTY_FILTERS, page: 0 });
    };

    const rows = dynamicPricingList?.data || (Array.isArray(dynamicPricingList) ? dynamicPricingList : []);

    return (
        <>
            <DynamicPricingSubHeader />
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[200px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Msn</label>
                    <input
                        type="text"
                        placeholder="Search by exact MSN"
                        value={msnInput}
                        onChange={(e) => setMsnInput(e.target.value)}
                        onKeyDown={onFilterKeyDown}
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                    />
                </div>
                <div className="w-[190px]">
                    <label className="block text-[11px] text-gray-600 mb-1">L1 Category</label>
                    <Select
                        isMulti
                        options={l1Options}
                        value={selectedL1}
                        onChange={(val: any) => setSelectedL1(val || [])}
                        placeholder="All"
                        styles={SELECT_STYLES}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="w-[190px]">
                    <label className="block text-[11px] text-gray-600 mb-1">L2 Category</label>
                    <Select
                        isMulti
                        options={l2Options}
                        value={selectedL2}
                        onChange={(val: any) => setSelectedL2(val || [])}
                        placeholder="All"
                        isDisabled={l2Options.length === 0}
                        styles={SELECT_STYLES}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="w-[190px]">
                    <label className="block text-[11px] text-gray-600 mb-1">L3 Category</label>
                    <Select
                        isMulti
                        options={l3Options}
                        value={selectedL3}
                        onChange={(val: any) => setSelectedL3(val || [])}
                        placeholder="All"
                        isDisabled={l3Options.length === 0}
                        styles={SELECT_STYLES}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="w-[190px]">
                    <label className="block text-[11px] text-gray-600 mb-1">L4 Category</label>
                    <Select
                        isMulti
                        options={l4Options}
                        value={selectedL4}
                        onChange={(val: any) => setSelectedL4(val || [])}
                        placeholder="All"
                        isDisabled={l4Options.length === 0}
                        styles={SELECT_STYLES}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div className="w-[110px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Min Price</label>
                    <input
                        type="number"
                        placeholder="0"
                        value={priceFilters.minPrice}
                        onChange={(e) => setPriceFilters({ ...priceFilters, minPrice: e.target.value })}
                        onKeyDown={onFilterKeyDown}
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                    />
                </div>
                <div className="w-[110px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Max Price</label>
                    <input
                        type="number"
                        placeholder="Any"
                        value={priceFilters.maxPrice}
                        onChange={(e) => setPriceFilters({ ...priceFilters, maxPrice: e.target.value })}
                        onKeyDown={onFilterKeyDown}
                        className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full"
                    />
                </div>
                <button
                    onClick={onSearch}
                    className="h-8 px-5 rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
                >
                    Search
                </button>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
            </div>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            {(DYNAMIC_PRICING_PAGE_HEADERS || []).map((header: string) => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-white whitespace-nowrap">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={(DYNAMIC_PRICING_PAGE_HEADERS || []).length} className="py-12">
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Loader />
                                        <span className="text-xs text-gray-500">Loading mappings...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <DynamicPricingListItem data={rows} />
                        )}
                    </tbody>
                </table>
            </div>
            {!isSearchingMsn && rows.length > 0 && (
                <CommonPagination
                    data={dynamicPricingList}
                    PAGE_SIZE={GET_DYNAMIC_PRICING_DEFAULT_PAGE_SIZE}
                    onPageChange={onPageChange}
                />
            )}
        </>
    );
};

export default DynamicPricingList;
